package co.maytes.standupbot.dashboard.features.standups

import co.maytes.standupbot.shared.util.RelativeTime
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

fun humanizeCron(cron: String): String {
    val parts = cron.trim().split(Regex("\\s+"))
    if (parts.size < 6) return cron
    val minute = parts[1]
    val hour = parts[2]
    val dow = parts[5]
    val time = formatClockTime(hour, minute) ?: return cron
    val day = formatDaysOfWeek(dow) ?: return cron
    return "$day · $time"
}

fun shortTimezone(raw: String): String {
    val tz = runCatching { TimeZone.of(raw) }.getOrDefault(TimeZone.UTC)
    return shortTimezone(tz, raw)
}

fun shortTimezone(tz: TimeZone, raw: String): String {
    val short = raw.substringAfterLast("/").replace("_", " ")
    return if (short.isBlank()) tz.id else short
}

fun nextRunDisplay(
    next: Instant,
    now: Instant,
    tz: TimeZone,
): String {
    val relative = RelativeTime.formatFuture(next, now)
    val local = next.toLocalDateTime(tz)
    val today = now.toLocalDateTime(tz).date
    val tomorrow = today.plus(1, DateTimeUnit.DAY)
    val prefix = when (local.date) {
        today -> "Today"
        tomorrow -> "Tomorrow"
        else -> local.date.toString()
    }
    val hh = local.hour.toString().padStart(2, '0')
    val mm = local.minute.toString().padStart(2, '0')
    return "$prefix $hh:$mm · $relative"
}

private fun formatClockTime(hourField: String, minuteField: String): String? {
    val h = hourField.toIntOrNull() ?: return null
    val m = minuteField.toIntOrNull() ?: return null
    val ampm = if (h < 12) "AM" else "PM"
    val h12 = when {
        h == 0 -> 12
        h > 12 -> h - 12
        else -> h
    }
    val mm = m.toString().padStart(2, '0')
    return "$h12:$mm $ampm"
}

private fun formatDaysOfWeek(field: String): String? {
    if (field == "?" || field == "*") return "Every day"
    val tokens = field.split(",").map { it.trim() }
    val canonical = tokens.map { it.uppercase() }
    val weekdays = listOf("MON", "TUE", "WED", "THU", "FRI")
    val weekend = listOf("SAT", "SUN")
    if (canonical.sorted() == weekdays.sorted()) return "Weekdays"
    if (canonical.sorted() == weekend.sorted()) return "Weekends"
    if (canonical.sorted() == (weekdays + weekend).sorted()) return "Every day"
    if (canonical.size == 1) {
        val one = canonical.single()
        if (one == "MON-FRI") return "Weekdays"
        if (one == "SAT-SUN") return "Weekends"
        dayRangeOrNull(one)?.let { return it }
        return dayLabel(one)
    }
    val mapped = canonical.mapNotNull(::dayLabel)
    if (mapped.size != canonical.size) return null
    return mapped.joinToString(", ")
}

private fun dayRangeOrNull(token: String): String? {
    val range = token.split("-").takeIf { it.size == 2 } ?: return null
    val start = dayLabel(range[0]) ?: return null
    val end = dayLabel(range[1]) ?: return null
    return "$start–$end"
}

private fun dayLabel(token: String): String? = when (token.uppercase()) {
    "MON", "1" -> "Mon"
    "TUE", "2" -> "Tue"
    "WED", "3" -> "Wed"
    "THU", "4" -> "Thu"
    "FRI", "5" -> "Fri"
    "SAT", "6" -> "Sat"
    "SUN", "0", "7" -> "Sun"
    else -> null
}
